import { ColumnTable, Icon } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'reactstrap';

import { ScheduleList } from './SectionForm';

type Props = {
  openCreateOrUpdate: (index?: number) => void;
  onDelete: (index: number) => void;
  deletingIndex?: number | null;
  isReadOnly?: boolean;
};

export const useColumnsSectionSchedule = ({
  openCreateOrUpdate,
  onDelete,
  deletingIndex,
  isReadOnly,
}: Props) => {
  const prefix = 'sectionsMaintainer.update.sectionScheduleColumns';
  const { t } = useTranslation();
  const tUntilModule = useMemo(() => {
    return t(`${prefix}.untilModule`);
  }, [t]);

  const columns: ColumnTable<ScheduleList>[] = useMemo(() => {
    return [
      {
        columnName: 'activity',
        width: '20%',
        thClassName: 'text-center',
        tdClassName: 'text-center text-nowrap',
        headerText: t(`${prefix}.classType`),
        cellFormat: ({ row }) => row.activity.label,
      },
      {
        columnName: 'day',
        width: '10%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.day`),
        cellFormat: ({ row }) => t(`common.days.${row.day.value}`),
      },
      {
        columnName: 'periodicity',
        width: '10%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.periodicity`),
        cellFormat: ({ row }) => row.lessonsPeriodicity?.label || '',
      },
      {
        columnName: 'modules',
        width: '20%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.modules`),
        cellFormat: ({ row }) => {
          if (!row?.endModule) {
            return row.startModule.label;
          }
          return `${row.startModule.label} ${tUntilModule} ${
            row?.endModule?.label || row.startModule.label
          }`;
        },
      },
      {
        columnName: 'course',
        width: '20%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.classResponsible`),
        cellFormat: ({ row }) => row.responsible?.label,
      },
      {
        columnName: 'course',
        width: '20%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.classroomCampus`),
        cellFormat: ({ row }) =>
          `${row.classroom?.label ?? ''} ${
            row.campus?.label ? '- ' + row.campus?.label : ''
          }`,
      },
      {
        columnName: 'isActive',
        width: '20%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.state`),
        cellFormat: ({ value }) =>
          value ? t(`common.forms.active`) : t(`common.forms.inactive`),
      },
      {
        columnName: 'id',
        width: '10%',
        thClassName: 'text-center',
        tdClassName: 'text-center text-nowrap',
        headerText: t(`common.actions.action`),
        cellFormat: ({ index, row }) => {
          if (deletingIndex === index) {
            return (
              <div className="d-flex justify-content-center">
                <Spinner size="sm" />
              </div>
            );
          }
          if (!row.isActive || isReadOnly) {
            return null;
          }
          return (
            <div className="d-flex d-flex justify-content-center">
              <div
                className="cursor-pointer mr-2"
                onClick={() => {
                  openCreateOrUpdate(index);
                }}
              >
                <Icon size="15" name="edit" />
              </div>
              <div
                className="cursor-pointer"
                onClick={() => {
                  onDelete(index);
                }}
              >
                <Icon size="15" name="trash" />
              </div>
            </div>
          );
        },
      },
    ];
  }, [
    t,
    tUntilModule,
    openCreateOrUpdate,
    onDelete,
    deletingIndex,
    isReadOnly,
  ]);

  return columns;
};
